import type { Company } from "@/types/company";

type StorefrontCompany = Pick<Company, "slug"> & {
  custom_subdomain_enabled?: boolean;
  custom_subdomain_active?: boolean;
  custom_domain?: string | null;
  custom_domain_active?: string | null;
  custom_domain_status?:
    | "not_configured"
    | "pending"
    | "active"
    | "error";
};

function getPlatformBaseDomain(hostname: string) {
  if (
    hostname === "fornecedor.dev" ||
    hostname.endsWith(".fornecedor.dev")
  ) {
    return "fornecedor.dev";
  }

  if (
    hostname === "dev.fornecedor.net" ||
    hostname.endsWith(".dev.fornecedor.net")
  ) {
    return "dev.fornecedor.net";
  }

  return "fornecedor.net";
}

function normalizeCustomDomain(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }

  const hostname = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split(/[/?#]/, 1)[0]
    .split(":", 1)[0]
    .replace(/\.$/, "");

  if (!hostname) {
    return null;
  }

  return hostname.startsWith("www.")
    ? hostname
    : `www.${hostname}`;
}

function getActiveCustomDomain(
  company: StorefrontCompany,
): string | null {
  if (company.custom_domain_active) {
    return normalizeCustomDomain(
      company.custom_domain_active,
    );
  }

  if (
    company.custom_domain &&
    company.custom_domain_status === "active"
  ) {
    return normalizeCustomDomain(
      company.custom_domain,
    );
  }

  return null;
}

function hasActiveCustomSubdomain(
  company: StorefrontCompany,
): boolean {
  if (
    typeof company.custom_subdomain_active ===
    "boolean"
  ) {
    return company.custom_subdomain_active;
  }

  return company.custom_subdomain_enabled === true;
}

export function getStorefrontSubdomainUrl(
  company: Pick<StorefrontCompany, "slug">,
): string {
  if (typeof window === "undefined") {
    return `https://${company.slug}.fornecedor.net`;
  }

  const { hostname, protocol } = window.location;
  const baseDomain = getPlatformBaseDomain(hostname);

  return `${protocol}//${company.slug}.${baseDomain}`;
}

export function getStorefrontSubdomainDisplayUrl(
  company: Pick<StorefrontCompany, "slug">,
): string {
  return getStorefrontSubdomainUrl(company)
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
}

export function getStorefrontUrl(
  company: StorefrontCompany,
): string {
  const customDomain =
    getActiveCustomDomain(company);

  if (customDomain) {
    return `https://${customDomain}`;
  }

  if (hasActiveCustomSubdomain(company)) {
    return getStorefrontSubdomainUrl(company);
  }

  if (typeof window === "undefined") {
    return `/${company.slug}`;
  }

  return `${window.location.origin}/${company.slug}`;
}

export function getStorefrontDisplayUrl(
  company: StorefrontCompany,
): string {
  return getStorefrontUrl(company)
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
}
